import React from 'react';
import { motion, Reorder } from 'framer-motion';
import { GripVertical, Settings, Trash } from 'lucide-react';
import { WorkoutExercise } from '../../../types/exercises';
import { exerciseLibrary } from '../../../data/exerciseLibrary';

interface ExerciseListProps {
  exercises: WorkoutExercise[];
  onUpdate: (exercises: WorkoutExercise[]) => void;
}

export function ExerciseList({ exercises, onUpdate }: ExerciseListProps) {
  const handleRemoveExercise = (index: number) => {
    const newExercises = [...exercises];
    newExercises.splice(index, 1);
    onUpdate(newExercises);
  };

  const getExerciseDetails = (exerciseId: string) => {
    return exerciseLibrary.find(e => e.id === exerciseId);
  };

  return (
    <Reorder.Group
      axis="y"
      values={exercises}
      onReorder={onUpdate}
      className="space-y-2"
    >
      {exercises.map((exercise, index) => {
        const details = getExerciseDetails(exercise.exerciseId);
        if (!details) return null;

        return (
          <Reorder.Item
            key={exercise.exerciseId}
            value={exercise}
            className="bg-muted/30 rounded-lg overflow-hidden"
          >
            <div className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
              
              <div className="flex-1">
                <div className="font-medium">{details.name}</div>
                <div className="text-sm text-muted-foreground">
                  {exercise.sets.length} {exercise.sets.length === 1 ? 'set' : 'sets'}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {/* Open exercise settings */}}
                  className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-muted-foreground" />
                </button>
                <button
                  onClick={() => handleRemoveExercise(index)}
                  className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Trash className="w-4 h-4 text-destructive" />
                </button>
              </div>
            </div>

            {/* Sets */}
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {exercise.sets.map((set, setIndex) => (
                  <div
                    key={setIndex}
                    className="flex items-center gap-4 text-sm"
                  >
                    <div className="w-8 text-center text-muted-foreground">
                      {setIndex + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <span className="text-muted-foreground">Reps: </span>
                        {set.reps || '-'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Weight: </span>
                        {set.weight || '-'}kg
                      </div>
                      <div>
                        <span className="text-muted-foreground">Rest: </span>
                        {set.restAfter}s
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reorder.Item>
        );
      })}
    </Reorder.Group>
  );
}